import { useEffect, useRef } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { celebrateSmall } from '../utils/confetti';
import { todayIso } from '../utils/format';
import {
  DONE_COIN,
  STEP_COIN,
  OnboardingStep,
  OnboardingStepKey,
  claimedSteps,
  onboardingDone,
  stepEventId,
} from '../utils/onboarding';

/**
 * Клеймит монеты за шаги онбординга и итоговый бонус.
 *
 * Вызывается ВНУТРИ OnboardingCard. Гейт showOnboarding проверяется
 * снаружи на уровне рендера — хук здесь вызывается безусловно (правила хуков),
 * но ранний выход гасит все эффекты если steps пуст или null.
 *
 * Тройная защита от дублей (по аналогии с useDailyClaim):
 *   1. `attemptedFor` ref — сессионный дедупликатор по ключу шага.
 *   2. `claimedSteps(viewData.game_events)` — снапшот уже содержит клейм.
 *   3. addGameEvent идемпотентен на API: existing → тишина.
 */
export function useOnboardingClaims(steps: OnboardingStep[] | null): void {
  const { status, viewData, addGameEvent } = useBudget();
  const toast = useToast();
  // Сессионный дедупликатор: ключ шага или 'ob_done'
  const attemptedFor = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (status !== 'ready' || !viewData || !steps || steps.length === 0) return;

    const month = todayIso().slice(0, 7);
    const events = viewData.game_events ?? [];
    const claimed = claimedSteps(events);
    const isDone = onboardingDone(events);

    // Клеймим каждый выполненный, но ещё не заклеймленный шаг
    for (const step of steps) {
      if (!step.done) continue;
      const key: OnboardingStepKey = step.key;
      const eventId = stepEventId(key);

      // Слой 1: сессионный дедупликатор
      if (attemptedFor.current.has(key)) continue;
      // Слой 2: снапшот уже содержит клейм
      if (claimed.has(key)) {
        attemptedFor.current.add(key);
        continue;
      }

      attemptedFor.current.add(key);
      void addGameEvent({
        id: eventId,
        type: 'onboarding_step',
        month,
        week: key,
        coins: STEP_COIN,
        payload: JSON.stringify({ step: key }),
      }).then((r) => {
        // Слой 3: existing → тишина
        if (r.existing) return;
        toast.reward({
          title: step.title,
          amount: `+${STEP_COIN}`,
          crit: false,
          sub: r.synced ? undefined : 'синх позже',
        });
      });
    }

    // Проверяем завершение всех 5 шагов
    const allDone = steps.length === 5 && steps.every((s) => s.done);
    if (allDone && !isDone) {
      const doneKey = 'ob_done';
      // Слой 1: сессионный дедупликатор
      if (attemptedFor.current.has(doneKey)) return;
      attemptedFor.current.add(doneKey);

      void addGameEvent({
        id: 'ob_done',
        type: 'onboarding_done',
        month,
        week: 'done',
        coins: DONE_COIN,
        payload: '{}',
      }).then((r) => {
        // Слой 3: existing → тишина
        if (r.existing) return;
        celebrateSmall();
        toast.reward({
          title: 'Первая неделя пройдена',
          amount: `+${DONE_COIN}`,
          crit: true,
          sub: r.synced ? undefined : 'синх позже',
        });
      });
    }
  }, [status, viewData, steps, addGameEvent, toast]);
}
